import { ChoiceList, InfoCircleOutlineIcon } from "@cmsgov/ds-medicare-gov";
import classNames from "classnames";
import React, { SyntheticEvent, useContext } from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import {
  AnalyticsActionType,
  Ga4Event,
  Ga4EventType,
} from "../../../app/contexts/Analytics/types";
import routes from "../../../app/routes";
import { AppContext } from "../../../app/store";
import { useTranslate } from "../../../helpers";
import { newBasicHelperDrawerAnalytics } from "../../BasicHelperDrawerAnalytics";
import HelpDrawerWrapper from "../../HelpDrawerWrapper";
import { choiceIdMap } from "../constants";
import { ErrorState } from "../CoverageSelector.v2";
import { CoverageType } from "../types";

interface PlanTypePageProps {
  isInModal?: boolean;
  planTypeErrorState: ErrorState;
  onPlanTypeChange: (value: CoverageType) => void;
}

export const PlanTypeStep: React.FunctionComponent<PlanTypePageProps> = ({
  isInModal,
  planTypeErrorState,
  onPlanTypeChange,
}) => {
  const t = useTranslate();
  const { dispatch } = useContext(AppContext);
  const helpDrawerToggleText = t("coverage_selector.help_drawer.toggle");
  const helpDrawerTitle = t("coverage_selector.help_drawer.title");
  const helperDrawerAnalytics = newBasicHelperDrawerAnalytics({
    customGA4OpenSettings: {
      event_name: Ga4Event.HELP_DRAWER_OPENED,
      event_type: Ga4EventType.UI_INTERACTION,
      heading: helpDrawerTitle,
      text: helpDrawerToggleText,
    },
    customGA4CloseSettings: {
      event_name: Ga4Event.HELP_DRAWER_CLOSED,
      event_type: Ga4EventType.UI_INTERACTION,
      heading: helpDrawerTitle,
    },
    customTealiumOpenSettings: {
      event_action: "help drawer opened",
      event_label: helpDrawerTitle,
      event_category: "ui interaction",
    },
    customTealiumCloseSettings: {
      event_action: "help drawer closed",
      event_label: helpDrawerTitle,
      event_category: "ui interaction",
    },
    dispatch,
  });
  const needHelpText = t("coverage_selector.help_drawer.help_link_text");
  const needHelpLinkUrl = routes.coverageWizard.options;
  return (
    <div
      className="mct-c-coverage-selector-v2__plantype"
      data-testid="plan-type-section"
    >
      <ChoiceList
        className="ds-u-margin-top--0"
        choices={[
          {
            label: (
              <div data-testid={choiceIdMap[CoverageType.MAPD]}>
                <span
                  className={classNames({
                    "mct-c-coverage-selector-v2__plan-type-sub-heading": isInModal,
                  })}
                >
                  <FormattedMessage
                    id="coverage_selector.plan_types.part_c.heading"
                    tagName="div"
                    values={{
                      message: (
                        <span
                          aria-label="Cee"
                          className="mct-c-coverage-selector-v2__plan-type-sub-heading-aria"
                        >
                          C
                        </span>
                      ),
                    }}
                  />
                </span>
                {isInModal && (
                  <p className="ds-u-margin-top--0">
                    {t("coverage_selector.help_drawer.part_c.body")}
                  </p>
                )}
              </div>
            ),
            value: CoverageType.MAPD,
            id: choiceIdMap[CoverageType.MAPD],
          },
          {
            label: (
              <div data-testid={choiceIdMap[CoverageType.PDP]}>
                <span
                  className={classNames({
                    "mct-c-coverage-selector-v2__plan-type-sub-heading": isInModal,
                  })}
                >
                  <FormattedMessage
                    id="coverage_selector.plan_types.part_d.heading"
                    tagName="div"
                    values={{
                      message: (
                        <span
                          aria-label="Dee"
                          className="mct-c-coverage-selector-v2__plan-type-sub-heading-aria"
                        >
                          D
                        </span>
                      ),
                    }}
                  />
                </span>
                {isInModal && (
                  <p className="ds-u-margin-top--0">
                    {t("coverage_selector.help_drawer.part_d.body")}
                  </p>
                )}
              </div>
            ),
            value: CoverageType.PDP,
            id: choiceIdMap[CoverageType.PDP],
          },
          {
            label: (
              <div data-testid={choiceIdMap[CoverageType.MedigapPolicy]}>
                <span
                  className={classNames({
                    "mct-c-coverage-selector-v2__plan-type-sub-heading": isInModal,
                  })}
                >
                  {t("coverage_selector.plan_types.medigap.heading")}
                </span>
                {isInModal && (
                  <p className="ds-u-margin-top--0">
                    {t("coverage_selector.help_drawer.medigap.body")}
                  </p>
                )}
              </div>
            ),
            value: CoverageType.MedigapPolicy,
            id: choiceIdMap[CoverageType.MedigapPolicy],
          },
          {
            label: (
              <div data-testid={choiceIdMap[CoverageType.PDPAndMedigap]}>
                <span
                  className={classNames({
                    "mct-c-coverage-selector-v2__plan-type-sub-heading": isInModal,
                  })}
                >
                  <FormattedMessage
                    id="coverage_selector.plan_types.part_d_and_medigap.heading"
                    tagName="div"
                    values={{
                      message: (
                        <span
                          aria-label="Dee"
                          className="mct-c-coverage-selector-v2__plan-type-sub-heading-aria"
                        >
                          D
                        </span>
                      ),
                    }}
                  />
                </span>
                {isInModal && (
                  <p className="ds-u-margin-top--0">
                    {t("coverage_selector.help_drawer.part_d_and_medigap.body")}
                  </p>
                )}
              </div>
            ),
            value: CoverageType.PDPAndMedigap,
            id: choiceIdMap[CoverageType.PDPAndMedigap],
          },
        ]}
        errorMessage={t(planTypeErrorState.message || "")}
        errorMessageClassName="mct-c-coverage-selector-v2__plantype-error-message"
        errorPlacement="bottom"
        label={
          <h2 className="ds-text-heading--md ds-u-measure--narrow ds-u-margin-bottom--2">
            {t("coverage_selector.plan_type.heading")}
          </h2>
        }
        name="coverage-selector-select-plan-type"
        onChange={(e: SyntheticEvent<HTMLInputElement>) => {
          onPlanTypeChange(e.currentTarget.value as CoverageType);
        }}
        type="radio"
      />
      {isInModal ? (
        <Link
          className="ds-u-margin-y--1 ds-u-display--block"
          to={needHelpLinkUrl}
          onClick={() => {
            dispatch({
              type: AnalyticsActionType.SEND_INTERNAL_LINK_CLICKED_EVENT,
              settings: {
                linkText: needHelpText,
                linkUrl: needHelpLinkUrl,
              },
            });
          }}
        >
          {needHelpText}
        </Link>
      ) : (
        <span
          className="ds-text-body--md ds-u-margin-y--1 ds-u-display--block"
          data-testid="need-help-text"
        >
          <HelpDrawerWrapper
            toggleText={
              <>
                <span className="ds-u-margin-right--1">
                  {helpDrawerToggleText}
                </span>
                <InfoCircleOutlineIcon />
              </>
            }
            toggleClassName="mct-c-coverage-selector-v2__help-drawer-toggle"
            mctAnalytics={helperDrawerAnalytics}
            drawerTitle={helpDrawerTitle}
          >
            <div>
              <h4 className="ds-u-margin-bottom--0">
                {t("coverage_selector.help_drawer.part_c.sub_heading")}
              </h4>
              <p className="ds-u-margin-top--1">
                {t("coverage_selector.help_drawer.part_c.body")}
              </p>
              <h4 className="ds-u-margin-bottom--0">
                {t("coverage_selector.help_drawer.part_d.sub_heading")}
              </h4>
              <p className="ds-u-margin-top--1">
                {t("coverage_selector.help_drawer.part_d.body")}
              </p>
              <h4 className="ds-u-margin-bottom--0">
                {t("coverage_selector.help_drawer.medigap.sub_heading")}
              </h4>
              <p className="ds-u-margin-top--1">
                {t("coverage_selector.help_drawer.medigap.body")}
              </p>
              <h4 className="ds-u-margin-bottom--0">
                {t(
                  "coverage_selector.help_drawer.part_d_and_medigap.sub_heading"
                )}
              </h4>
              <p className="ds-u-margin-top--1">
                {t("coverage_selector.help_drawer.part_d_and_medigap.body")}
              </p>
              <Link
                to={needHelpLinkUrl}
                onClick={() => {
                  dispatch({
                    type: AnalyticsActionType.SEND_INTERNAL_LINK_CLICKED_EVENT,
                    settings: {
                      linkText: needHelpText,
                      linkUrl: needHelpLinkUrl,
                    },
                  });
                }}
              >
                {needHelpText}
              </Link>
            </div>
          </HelpDrawerWrapper>
        </span>
      )}
    </div>
  );
};
